/**
 * useLocationPage — Reusable hook for location-specific service pages
 *
 * Usage:
 *   import { LocationPage } from './useLocationPage';
 *
 *   <LocationPage config={sarasotaConfig} />
 *
 * Pass a config object (see PROP REFERENCE at bottom of this file).
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { GiPalmTree } from 'react-icons/gi';

// ─── Sub-components assumed to exist in your project ─────────────────────────
import SlideInDiv from './slider.js';
import SlideInDivLeft from './slider2.js';
import UserModal from './UserModal.js';
import { useScrollBehavior } from './useScrollBehavior.js';
import Testimonials from './testimonials.js';
import Faqs from './faqs.js';
import GoogleMapSection from './GoogleMap.js';
import Footer from './footer4.js';

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useLocationPage() {
  const [isVisibleLinks, setIsVisibleLinks] = useState(false);
  const [isVisibleText, setIsVisibleText] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const linksRef = useRef(null);
  const textRef = useRef(null);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    const handleScroll = () => {
      if (linksRef.current) {
        const rect = linksRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) setIsVisibleLinks(true);
      }
      if (textRef.current) {
        const rect = textRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight - 300) setIsVisibleText(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return {
    isVisibleLinks,
    isVisibleText,
    isModalOpen,
    openModal,
    closeModal,
    linksRef,
    textRef,
  };
}

// ─── Default service buttons (can be overridden via config) ───────────────────
const DEFAULT_BUTTONS = [
  { label: 'Tree Trimming', to: '/tree_trimming' },
  { label: 'Tree Removal',  to: '/tree_removal'  },
  { label: 'Stump Removal', to: '/stump_removal'  },
  { label: 'Debris Hauling',to: '/debris_hauling' },
];

// ─── Default feature cards (fixed text per spec — do NOT override) ────────────
const FEATURE_CARDS = [
  {
    delay: '.3s',
    icon: <i className="bi bi-houses" style={{ fontSize: '2rem', color: 'green' }} />,
    heading: 'Residential Services',
    body: `We offer expert tree care for your home, including trimming,
           pruning, and safe tree removal. Our team ensures your trees
           remain healthy, enhancing your property's beauty and safety.`,
  },
  {
    delay: '.6s',
    icon: <i className="bi bi-buildings" style={{ fontSize: '2rem', color: 'green' }} />,
    heading: 'Commercial',
    body: `We provide safe,
           efficient, and budget-friendly tree removal services
           tailored to meet your commercial needs.`,
  },
  {
    delay: '.9s',
    icon: <i className="bi bi-exclamation-diamond" style={{ fontSize: '2rem', color: 'green' }} />,
    heading: '24/7 Emergency Services',
    body: `Trust us anytime, day or night, for fast and dependable tree care,
           safeguarding your property and loved ones when it matters most.`,
  },
];

// ─── Main Component ────────────────────────────────────────────────────────────
/**
 * @param {Object}   config
 *
 * SEO / meta
 * @param {string}   config.pageTitle          — <title> tag, e.g. "Sarasota Service | Sam's Tree Service"
 * @param {string}   config.metaDescription    — <meta name="description">
 * @param {string}   config.canonicalUrl       — <link rel="canonical">
 *
 * Hero section
 * @param {string}   config.heroHeading        — Large heading, e.g. "Serving Sarasota"
 * @param {string}   [config.heroImageUrl]     — Hero background image path (default: ../images/out_25.webp)
 * @param {string}   [config.bgImageUrl]       — Full-page fixed BG image (default: ../images/image8.webp)
 *
 * Main content section
 * @param {string}   config.sectionHeading     — H1 inside the text panel
 * @param {string[]} config.paragraphs         — Array of paragraph strings (supports <strong> via dangerouslySetInnerHTML)
 * @param {string}   config.ctaLabel           — CTA button text, e.g. "Request An Appointment"
 *
 * Slide images
 * @param {string}   [config.slideImage1]      — Left large slide image
 * @param {string}   [config.slideImage2]      — Top-right slide image
 * @param {string}   [config.slideImage3]      — Bottom-right slide image
 *
 * Service buttons (optional override)
 * @param {Array<{label:string, to:string}>} [config.serviceButtons]
 *
 * Google Map
 * @param {string}   config.mapCity            — e.g. "Sarasota, FL"
 *
 * CSS class prefix (optional, for multi-location isolation)
 * @param {string}   [config.cssPrefix]        — e.g. "sarasota" → IDs/classes use this prefix
 */
export function LocationPage({ config = {} }) {
  useScrollBehavior();

  const {
    isVisibleLinks,
    isVisibleText,
    isModalOpen,
    openModal,
    closeModal,
    linksRef,
    textRef,
  } = useLocationPage();

  const {
    pageTitle        = 'Service Page | Sam\'s Tree Service',
    metaDescription  = 'Professional tree service.',
    canonicalUrl     = '',
    heroHeading      = 'Serving Your Area',
    heroImageUrl     = require('../images/out_25.webp'),
    bgImageUrl       = require('../images/image8.webp'),
    sectionHeading   = 'Professional Tree Service',
    paragraphs       = [],
    ctaLabel         = 'Request An Appointment',
    slideImage1      = require('../images/image120.webp'),
    slideImage2      = require('../images/image36.webp'),
    slideImage3      = require('../images/image97_.webp'),
    serviceButtons   = DEFAULT_BUTTONS,
    mapCity          = 'Your City, FL',
  } = config;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Helmet>

      <div className="lp-root">
        {/* ── Fixed background ── */}
        <div
          className="lp-bg"
          style={{ backgroundImage: `url(${bgImageUrl})` }}
        />

        {/* ══════════════════════════════════════════════
            MODULE 1 — Hero
        ══════════════════════════════════════════════ */}
        <section className="lp-module lp-hero">
          <div
            className="lp-hero__img"
            style={{ backgroundImage: `url(${heroImageUrl})` }}
          >
            <div className="lp-hero__overlay">
              <div className="lp-hero__title">
                <h1>{heroHeading}</h1>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════
            MODULE 2 — Content + Slide Images
        ══════════════════════════════════════════════ */}
        <section className="lp-module lp-content"> 

 {/* Feature cards — text is fixed per spec */}
            <div className="lp-features" ref={linksRef}>
              <div className="lp-features__inner">
                {FEATURE_CARDS.map(({ delay, icon, heading, body }) => (
                  <div
                    key={heading}
                    className={`lp-feature-card${isVisibleLinks ? ' lp-feature-card--visible' : ''}`}
                    style={{ transitionDelay: delay }}
                  >
                    <h1 className="lp-feature-card__heading">
                      {icon}&nbsp;{heading}
                    </h1>
                    <p className="lp-feature-card__body">{body}</p>
                  </div>
                ))}
              </div>
            </div>

          <div className="lp-content__inner">

            {/* Slide images */}
            <div className="lp-slides">
              <SlideInDiv>
                <div
                  className="lp-slide lp-slide--main"
                  style={{ backgroundImage: `url(${slideImage1})` }}
                />
              </SlideInDiv>
              <div className="lp-slides__stack">
                <SlideInDivLeft>
                  <div
                    className="lp-slide lp-slide--top"
                    style={{ backgroundImage: `url(${slideImage2})` }}
                  />
                </SlideInDivLeft>
                <SlideInDivLeft>
                  <div
                    className="lp-slide lp-slide--bottom"
                    style={{ backgroundImage: `url(${slideImage3})` }}
                  />
                </SlideInDivLeft>
              </div>
            </div>

           

            {/* Text panel */}
            <div
              ref={textRef}
              className={`lp-text-panel${isVisibleText ? ' lp-text-panel--visible' : ''}`}
            >
              {/* Decorative palm icon row */}
              <div className="lp-icon-row">
                <h1 className="lp-dashes">- - - - - -&nbsp;&nbsp;</h1>
                <GiPalmTree className="lp-palm" />
                <h1 className="lp-dashes">&nbsp;&nbsp;- - - - - -</h1>
              </div>

              <h1 className="lp-text-panel__heading">{sectionHeading}</h1>

              {paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="lp-text-panel__body"
                  dangerouslySetInnerHTML={{ __html: p }}
                />
              ))}

              
            </div>
             {/* Service buttons */}
            <div className="lp-btn-row">
              {serviceButtons.map(({ label, to }) => (
                <Link key={to} to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="lp-service-btn">{label}</div>
                </Link>
              ))}
            </div>
            <div id="lp-cta-btn-con"> 
                <div
                className="lp-cta-btn"
                onClick={(e) => { e.preventDefault(); openModal(); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openModal()}
              >
                {ctaLabel}
              </div>
            </div>
             
           
          </div>

          {isModalOpen && <UserModal onClose={closeModal} />}
          <UserModal isOpen={isModalOpen} onClose={closeModal} />
        </section>

        {/* ══════════════════════════════════════════════
            MODULE 3 — Testimonials
        ══════════════════════════════════════════════ */}
        <section className="lp-module lp-testimonials">
          <Testimonials />
        </section>

        {/* ══════════════════════════════════════════════
            MODULE 4 — FAQs
        ══════════════════════════════════════════════ */}
        <section className="lp-module lp-faqs">
          <Faqs />
        </section>

        {/* ══════════════════════════════════════════════
            MODULE 5 — Google Map
        ══════════════════════════════════════════════ */}
        <section className="lp-module lp-map">
          <GoogleMapSection city={mapCity} />
        </section>

        <Footer />
      </div>
    </>
  );
}

export default LocationPage;